/**
 * queue-columns — per-segment column definitions. Each queue state shows only
 * the columns that carry meaning for it (queued has no stats; eliminated has
 * no cap, etc.). Cells are pure render fns fed a shared QueueCellCtx.
 * For Media Devoted | Created By Ciro Autiero
 */

import { ReactNode } from 'react';
import type {
  DemoteDestination,
  QueueElement,
  QueuePoolSummary,
} from '@trafficpulse/shared';

import { PriorityControl } from '@/components/ui';
import { formatDateTimeGb } from '@/lib/utils';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { humanizeDuration, testedForLabel } from '../queue-status.util';
import { poolElementKey } from '../rotation/rotation-swaps';
import { type QueueSegment } from '../queue-filters';
import { PathStatusBadge } from '../status';
import RequeueCell from '../eliminated/RequeueCell';
import DemoteCell from '../live/DemoteCell';
import WaitingActionsCell from './waiting/WaitingActionsCell';
import { waitingSinceOf } from './waiting/waiting-dates';
import SelectCheckbox from '../bulk/row/SelectCheckbox';
import type { BulkRowProps } from '../bulk/row/bulk-row.types';
import QueueTimelineCell from '../timeline/QueueTimelineCell';
import QueueCapCell, { findPool } from './QueueCapCell';
import QueueElementCapChips from '../../../components/cap-usage/QueueElementCapChips';
import QueueStatsCell from './QueueStatsCell';
// Imported by path, not through ./sort — the barrel pulls in queue-sort, which
// needs QueueColumn from this file, and that would be a cycle.
import {
  epochOf,
  ruleSortValue,
  testedForSeconds,
  trackerSortValue,
} from './sort/queue-sort-values';
import {
  timesQueuedCell,
  lastRestoredCell,
  trackerTruthCell,
} from './queue-smart-cells';
import {
  LayerCell,
  NameWithNextCell,
  RemovalReasonCell,
  RowActionsCell,
  RowName,
  RuleBadge,
  TrackerIdCell,
} from './queue-cells';

export interface QueueCellCtx {
  pools: QueuePoolSummary[];
  onSetPriority: (element: QueueElement, priority: number | null) => void;
  priorityDisabled: boolean;
  /** bankIds of the "next up for rotation" element per layer (highlighted). */
  nextUp: Set<string>;
  /** Queued bankId → name of the live element it replaces on the next run. */
  incoming: Map<string, string>;
  campaignId: string;
  provider: QueueProvider;
  /** Awaitable, so the row's confirm dialog can stay busy until it settles. */
  onRequeue: (
    element: QueueElement,
    to: 'queue' | 'waiting',
  ) => void | Promise<unknown>;
  requeuePending: boolean;
  onDemote: (element: QueueElement, to: DemoteDestination) => void;
  demotePending: boolean;
  /** Absent ⇒ no select column (the table also renders outside the Queue tab). */
  bulk?: BulkRowProps;
}

export interface QueueColumn {
  key: string;
  header: string;
  cell: (element: QueueElement, ctx: QueueCellCtx) => ReactNode;
  align?: 'right';
  /** Explains a column whose value isn't self-evident; shown behind an ⓘ. */
  hint?: string;
  /**
   * Present ⇒ the header becomes a sort button, sorting on THIS value rather
   * than on whatever the cell rendered. Absent ⇒ a plain header, exactly as
   * before — which is what keeps sorting off the segments that did not ask for
   * it, since this table also renders Live whenever a layer pill is active.
   */
  sortValue?: (el: QueueElement) => string | number | null;
  /** Dates and counts read better newest/most first on the first click. */
  sortDescFirst?: boolean;
}

/** Final tested-for on an eliminated element: recorded duration, else elapsed. */
function removedTestedFor(el: QueueElement): string {
  if (el.testDurationSeconds != null) return humanizeDuration(el.testDurationSeconds);
  return testedForLabel(el.testStartedAt);
}

const nowrap = (text: string): ReactNode => (
  <span className="whitespace-nowrap tabular-nums">{text}</span>
);

const CELLS = {
  select: (el, ctx) =>
    ctx.bulk ? (
      <SelectCheckbox
        element={el}
        checked={ctx.bulk.isSelected(el)}
        onToggle={ctx.bulk.onToggleSelect}
        disabled={!ctx.bulk.selectable}
          status={ctx.bulk.statusOf?.(el)}
      />
    ) : null,
  name: (el, ctx) => (
    <NameWithNextCell
      name={el.itemName}
      next={ctx.nextUp.has(el.bankId)}
      replaces={ctx.incoming.get(poolElementKey(el))}
      // Renders nothing unless the path is off / getting no traffic, so this
      // costs healthy rows neither a column nor a pixel.
      badge={<PathStatusBadge element={el} />}
    />
  ),
  layer: (el) => <LayerCell layer={el.layer} />,
  rule: (el) => <RuleBadge rule={el.rule} />,
  pool: (el) => <RowName name={el.poolName} />,
  cap: (el, ctx) => <QueueCapCell pool={findPool(ctx.pools, el.layer, el.poolId)} />,
  offerCap: (el, ctx) => <QueueElementCapChips element={el} provider={ctx.provider} />,
  priority: (el, ctx) => (
    <PriorityControl
      value={el.priority}
      onChange={(v) => ctx.onSetPriority(el, v)}
      disabled={ctx.priorityDisabled}
    />
  ),
  queuedAt: (el) => nowrap(formatDateTimeGb(el.queuedAt)),
  addedAt: (el) => nowrap(formatDateTimeGb(el.addedAt ?? null)),
  waitingSince: (el) => nowrap(formatDateTimeGb(waitingSinceOf(el))),
  waitingActions: (el, ctx) => (
    <WaitingActionsCell
      element={el}
      campaignId={ctx.campaignId}
      provider={ctx.provider}
    />
  ),
  timesQueued: (el) => timesQueuedCell(el),
  lastRestored: (el) => lastRestoredCell(el),
  tracker: (el) => trackerTruthCell(el),
  // Kept though no segment renders it today — Live dropped its "Tested for"
  // column; Eliminated still uses testedForFinal below.
  testedForLive: (el) => nowrap(testedForLabel(el.testStartedAt)),
  // Kept though no segment renders it today — Live dropped its Stats column and
  // this is the one line that brings it back.
  stats: (el) => <QueueStatsCell metrics={el.metrics} />,
  trackerId: (el) => <TrackerIdCell itemId={el.itemId} />,
  removedAt: (el) => nowrap(formatDateTimeGb(el.removedAt)),
  reason: (el) => <RemovalReasonCell element={el} />,
  testedForFinal: (el) => nowrap(removedTestedFor(el)),
  timeline: (el, ctx) => (
    <QueueTimelineCell
      element={el}
      campaignId={ctx.campaignId}
      provider={ctx.provider}
    />
  ),
  requeue: (el, ctx) => (
    <RowActionsCell timeline={CELLS.timeline(el, ctx)}>
      <RequeueCell
        element={el}
        onRequeue={ctx.onRequeue}
        pending={ctx.requeuePending}
      />
    </RowActionsCell>
  ),
  demote: (el, ctx) => (
    <RowActionsCell timeline={CELLS.timeline(el, ctx)}>
      <DemoteCell
        element={el}
        campaignId={ctx.campaignId}
        provider={ctx.provider}
        onDemote={ctx.onDemote}
        pending={ctx.demotePending}
      />
    </RowActionsCell>
  ),
} satisfies Record<string, QueueColumn['cell']>;

/** Everything past the cell is optional trim — kept in one bag so adding the
 *  next one never grows the signature. */
type ColOpts = Omit<QueueColumn, 'key' | 'header' | 'cell'>;

const col = (
  key: string,
  header: string,
  cell: QueueColumn['cell'],
  opts: ColOpts = {},
): QueueColumn => ({ key, header, cell, ...opts });

/**
 * Why Pool exists when it mirrors Rule. For a PATH the pool is built from the
 * path's own routing group, so the two columns can never disagree — the value
 * only earns its place on landers and offers, where the pool is the PATH an
 * element sits on rather than its rule.
 */
/** Not the pool's capacity slot ("Cap" on Queued) — the partner's cap on the
 *  path's offers, and how much of it the tracker has counted (MD-1147). */
const OFFER_CAP_HINT =
  'The partner cap on this path\'s offers — FTDs (or the cap type) counted on the tracker so far against the limit, per day / week / month or over the lifetime. Empty when no offer on the path carries a cap.';

const POOL_HINT =
  'The group this element competes in for a capacity slot. For paths that is the same routing group as Rule, so the two always match. It differs for landers and offers (coming soon), where the pool is the path the element sits on — two landers under one rule can compete in different pools.';

export const QUEUE_COLUMNS: Record<QueueSegment, QueueColumn[]> = {
  // Sorted newest-first by default: the question asked of this list is "what
  // landed here recently", and the flat unordered list it used to be answered
  // nothing. Both dates are shown because they answer different questions —
  // see table/waiting/waiting-dates.
  waiting: [
    col('select', '', CELLS.select),
    col('name', 'Name', CELLS.name, { sortValue: (el) => el.itemName }),
    col('layer', 'Layer', CELLS.layer, { sortValue: (el) => el.layer }),
    col('pool', 'Pool', CELLS.pool, {
      hint: POOL_HINT,
      sortValue: (el) => el.poolName,
    }),
    col('offerCap', 'Offer CAP', CELLS.offerCap, { hint: OFFER_CAP_HINT }),
    col('waitingSince', 'Waiting since', CELLS.waitingSince, {
      hint: 'When this element last landed in Waiting. For one that has never been live this is the day it was added, because that is how long it has been sitting here.',
      sortValue: (el) => epochOf(waitingSinceOf(el)),
      sortDescFirst: true,
    }),
    col('addedAt', 'Added', CELLS.addedAt, {
      hint: 'When the element was added to the bank. It never changes, however many times the element is promoted or parked.',
      sortValue: (el) => epochOf(el.addedAt ?? null),
      sortDescFirst: true,
    }),
    col('timesQueued', 'Times queued', CELLS.timesQueued, {
      align: 'right',
      sortValue: (el) => el.timesQueued,
      sortDescFirst: true,
    }),
    col('timeline', '', CELLS.timeline, { align: 'right' }),
    col('waitingActions', 'Actions', CELLS.waitingActions, { align: 'right' }),
  ],
  queued: [
    col('select', '', CELLS.select),
    col('name', 'Name', CELLS.name),
    col('layer', 'Layer', CELLS.layer),
    col('pool', 'Pool', CELLS.pool, { hint: POOL_HINT }),
    col('offerCap', 'Offer CAP', CELLS.offerCap, { hint: OFFER_CAP_HINT }),
    col('queuedAt', 'Queued at', CELLS.queuedAt),
    col('timesQueued', 'Times queued', CELLS.timesQueued, { align: 'right' }),
    col('lastRestored', 'Last restored', CELLS.lastRestored),
    col('cap', 'Cap', CELLS.cap, { align: 'right' }),
    col('tracker', 'Tracker', CELLS.tracker),
    col('timeline', '', CELLS.timeline, { align: 'right' }),
  ],
  // Live deliberately drops Stats, Times queued, Cap and Tested for: the row is
  // about what is running, which rule put it there, and what you can do with it.
  // Cap already shows on the segment header, and the rest answered questions
  // nobody asks here. Note this saves no backend work — the metrics cube is
  // fetched when a rotation rule or a capacity limit exists, never because a
  // column renders.
  testing: [
    col('select', '', CELLS.select),
    col('name', 'Name', CELLS.name),
    col('layer', 'Layer', CELLS.layer),
    col('rule', 'Rule', CELLS.rule),
    col('pool', 'Pool', CELLS.pool, { hint: POOL_HINT }),
    col('offerCap', 'Offer CAP', CELLS.offerCap, { hint: OFFER_CAP_HINT }),
    col('trackerId', 'Tracker id', CELLS.trackerId),
    col('demote', 'Actions', CELLS.demote, { align: 'right' }),
  ],
  // The only sortable segment. "What died last / lasted longest / kept coming
  // back" are the questions asked of this list, and none of them are answerable
  // from the promotion order it arrives in.
  removed: [
    col('select', '', CELLS.select),
    col('name', 'Name', CELLS.name, { sortValue: (el) => el.itemName }),
    col('layer', 'Layer', CELLS.layer, { sortValue: (el) => el.layer }),
    col('rule', 'Rule', CELLS.rule, { sortValue: ruleSortValue }),
    col('pool', 'Pool', CELLS.pool, {
      hint: POOL_HINT,
      sortValue: (el) => el.poolName,
    }),
    col('removedAt', 'Eliminated at', CELLS.removedAt, {
      sortValue: (el) => epochOf(el.removedAt),
      sortDescFirst: true,
    }),
    col('reason', 'Reason', CELLS.reason, {
      sortValue: (el) => el.removedReason,
    }),
    col('testedFor', 'Tested for', CELLS.testedForFinal, {
      sortValue: testedForSeconds,
      sortDescFirst: true,
    }),
    col('timesQueued', 'Times queued', CELLS.timesQueued, {
      align: 'right',
      sortValue: (el) => el.timesQueued,
      sortDescFirst: true,
    }),
    col('tracker', 'Tracker', CELLS.tracker, { sortValue: trackerSortValue }),
    col('requeue', 'Actions', CELLS.requeue, { align: 'right' }),
  ],
};
